import { buildHtmlString } from '@hotjar/insights-common/lib/html';

/**
 * Conditional feature enabling / disabling.
 */

/**
 * Mark a section as making up a feature.
 * You should use the state names (e.g. heatmap.list) as prefix to ensure feature names are unique.
 *
 * A feature is visible to all sites by default.
 * This can be changed by disabling the feature through /adm.
 * You can also set the require-explicit-permission to make the feature hidden by default,
 * and only shown if the site has the feature explicitly enabled.
 * Add the attribute hide-if-enabled to 'invert' a feature block.
 * This is useful to show other content to sites that do not have the feature.
 *
 * Enabling a feature by explicit permission can be done on an organization or a site level
 * through a manual update in the database.
 * It works with inheritance from the top down. This means that features enabled
 * on the organization level automatically becomes available to all sites in that organization.
 *
 * NOTE: We currently only support features on an organization and site level.
 *       This is because our user data is somewhat messed up and doesn't send an
 *       array of accounts in case the user is not an administrator.
 *
 * Usage examples:
 *
 * <div feature="heatmap.list">
 *     This will show unless "heatmap.list" is disabled through /adm.
 * </div>
 *
 * <div feature="heatmap.list.items">
 *     This sub-section within the list page will show unless "heatmap.list.items" is disabled through /adm.
 * </div>
 *
 * <div feature="heatmap.list.experimental" require-explicit-permission>
 *     This will only show if the site features includes "heatmap.list.experimental".
 * </div>
 *
 * <div feature="heatmap.list.experimental" hide-if-explicitly-enabled>
 *     This will hide if the site has the feature "heatmap.list.experimental" explicitly enabled.
 * </div>
 *
 * <div feature="heatmap.list" disable-feature>
 *     This will be replaced with the "this feature has been disabled" for everyone.
 * </div>
 *
 * <div feature="heatmap.list" site="model.currentSite">
 *     This will check feature for $scope.model.currentSite, instead of using the user's selected site.
 * </div>
 *
 */

angular.module('hjApp').directive('feature', [
    'Feature',
    'UserData',
    '$compile',
    function (Feature, $compile) {
        /**
         * Replace a section of HTML with a "feature unavailable" message.
         */
        function replaceSection(element, attrs, scope) {
            var elementNodeName = $(element)[0].nodeName.toLowerCase(),
                btnHtml;

            if (elementNodeName === 'div') {
                // This is an entire section. Replace it with a central message.
                $(element).html(
                    /* eslint-disable max-len */
                    '<div class="section-content centered-message">' +
                        '<div class="col-md-12">' +
                        '<div class="message">' +
                        '<div class="img"><img src="' +
                        require('@hotjar/shared-assets/images/section-maintenance.png') +
                        '" /></div>' +
                        '<div class="title">Scheduled Maintenance</div>' +
                        '<div class="sub-title mbn">We are currently performing some updates and have temporarily disabled this feature. Please check again in some time. We apologize for any inconvenience.</div>' +
                        '</div>' +
                        '</div>' +
                        '</div>'
                    /* eslint-enable max-len */
                );
            } else {
                // This is a <a> or <button> element.
                // Reconstruct the button, attaching some extra attributes.
                btnHtml = buildHtmlString({
                    elementType: elementNodeName,
                    innerHtml: $(element).html().trim(),
                    attributes: {
                        class: (attrs['class'] || '').replace('btn-primary', '') + ' disabled',
                        'data-tooltip-append-to-body': true,
                        'ng-if': attrs.ngIf,
                        'ng-show': attrs.ngShow,
                        'ng-hide': attrs.ngHide,
                        'ng-click': '',
                        onclick: 'return false;',
                        'tooltip-html-unsafe':
                            'We are currently performing some updates.<br />' +
                            '<strong>This action is temporarily unavailable</strong>.',
                        'tooltip-placement': attrs.tooltipPlacement || 'top',
                        type: attrs.type === 'submit' ? 'button' : null
                    }
                });

                $(element).replaceWith($compile(btnHtml)(scope));
            }
        }

        return {
            restrict: 'A',
            scope: {
                site: '='
            },
            link: function (scope, element, attrs) {
                var alreadyInitialized = false;

                // Since the site might change, we need to re-initialize this directive when it does.
                // The watch is also triggered the first the the variable is set, so we make sure
                // we don't initialize twice.
                scope.$watch('site', function () {
                    alreadyInitialized = true;
                    init();
                });

                // We add a tiny delay so that the $watch has had time to initialize if scope.site is set.
                setTimeout(function () {
                    if (!alreadyInitialized) {
                        init();
                    }
                }, 0);

                function init() {
                    var disableFeature = typeof attrs.disableFeature !== 'undefined',
                        hideIfExplicitlyEnabled = typeof attrs.hideIfExplicitlyEnabled !== 'undefined',
                        requireExplicitPermission = typeof attrs.requireExplicitPermission !== 'undefined',
                        gotExplicitPermission;

                    // In certain cases we need to pass a different site object and not use currentSite.
                    if (scope.site) {
                        gotExplicitPermission = Feature.isAllowed(attrs.feature, scope.site);
                    } else {
                        gotExplicitPermission = Feature.isAllowed(attrs.feature);
                    }

                    // Show an "unavailable" message if the feature is explicitly disabled through the html
                    // attribute disable-feature.
                    if (disableFeature) {
                        replaceSection(element, attrs, scope);
                    }

                    // If the site has the feature, but this is an 'inverted' condition and the
                    // element should be hidden instead.
                    if (hideIfExplicitlyEnabled && gotExplicitPermission) {
                        $(element).hide();
                        return;
                    } else {
                        $(element).show();
                    }

                    // If the feature requires explicit permission, only show it to sites that has that.
                    // This is so we can try out new features without making them publicly visible.
                    if (requireExplicitPermission && !gotExplicitPermission) {
                        $(element).hide();
                        return;
                    } else {
                        $(element).show();
                    }
                }
            }
        };
    }
]);
